import { escapeHtml } from "@/lib/html";

/**
 * Prompt rendering for the interactive shell.
 *
 *   - `promptFor(cwd)` returns the plain-text prompt
 *     (`[emily@byemily.org ~]$ `). The terminal's history uses
 *     this string and re-colorizes it in JSX; the editable input
 *     uses it to strip the prompt prefix from `textContent`
 *     when reading the user's input.
 *
 *   - `buildPromptHtml(cwd)` returns the colored HTML form of
 *     the same prompt. The cwd-update effect writes this into
 *     the prompt span via `innerHTML` so the contenteditable
 *     subtree stays structurally stable across React renders
 *     (and the user's caret is preserved).
 *
 * Keeping the two in lockstep is the whole point — `promptFor`
 * is the source of truth for the prefix length that
 * `textContent`-stripping uses, and `buildPromptHtml` paints
 * the same characters with the same grouping (so when the
 * history-prompt renderer re-colorizes `promptFor`'s output,
 * it lines up character-for-character).
 */

/** Build the plain-text prompt for the current cwd. The format
 *  matches the prompt the boot animation renders after login. */
export function promptFor(cwd: string): string {
  // Show the full cwd so deep paths stay unambiguous. `~` is
  // shown as `~`, anything else as the full `~/path` form. Real
  // shells collapse `$HOME` to `~` on display, but the bracketed
  // PS1 most users see prints the literal `~/...` tail so they
  // always know where they are.
  return `[emily@byemily.org ${cwd}]$ `;
}

/** Render the colored HTML form of the prompt. Same colors as
 *  the history-prompt renderer so the live prompt and the
 *  static history lines look identical. */
export function buildPromptHtml(cwd: string): string {
  const FG = "text-[color:var(--color-fg)]";
  const ACCENT = "text-[color:var(--color-accent)]";
  const BLUE = "text-[color:#5b81bb]";
  // The history-prompt renderer dumps the whole `~/path` tail
  // into one blue span, so the `/` separator inherits the same
  // blue color as `~` and the directory name. The live input
  // prompt here matches that: `~`, `/`, and the path tail are
  // all blue. Punctuation (`[`, `]`, `$`, space) stays fg.
  let pathHtml: string;
  if (cwd === "~") {
    pathHtml = `<span class="${BLUE}">~</span>`;
  } else if (!cwd.startsWith("~/")) {
    pathHtml = `<span class="${BLUE}">${escapeHtml(cwd)}</span>`;
  } else {
    pathHtml = `<span class="${BLUE}">~/${escapeHtml(cwd.slice(2))}</span>`;
  }
  return (
    `<span class="${FG}">[</span>` +
    `<span class="${ACCENT}">emily@byemily.org</span>` +
    `<span class="${FG}"> </span>` +
    pathHtml +
    `<span class="${FG}">]$ </span>`
  );
}
